import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../application/provider";
import { Button, Form, FormGroup, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap";
import { FileMinus } from "lucide-react";
import PagoDetraccionesService from "../axios_services/pagodetracciones.service";
import ProveedorServiciosService from "../axios_services/proveedorservicios.service";
import Loader from "../components/Loader/Loader";
import { ButtonSearch, ButtonInsert, ButtonUpdate, ButtonDelete, ButtonValidar } from "../components/Buttons/Buttons";
import ModalConfirmar from "../components/Modal/ModalConfirmar";
import "./PagoDetracciones.css";

const ESTADOS = [
    { id: 1, nombre: "Pendiente aprobación" },
    { id: 2, nombre: "Aprobado" },
    { id: 3, nombre: "Observado" },
    { id: 4, nombre: "Pago realizado" },
    { id: 5, nombre: "Concluido" },
    { id: 6, nombre: "Observado siguiente periodo" },
];

const MONTO_MINIMO_DETRACCION = 700;

const TIPOS_DETRACCION = [
    { id: "intermediacion_laboral", nombre: "Intermediación laboral y tercerización", porcentaje: 12 },
    { id: "arrendamiento_bienes", nombre: "Arrendamiento de bienes", porcentaje: 10 },
    { id: "mantenimiento_bienes_muebles", nombre: "Mantenimiento y reparación de bienes muebles", porcentaje: 12 },
    { id: "movimiento_carga", nombre: "Movimiento de carga", porcentaje: 10 },
    { id: "otros_servicios_empresariales", nombre: "Otros servicios empresariales", porcentaje: 12 },
    { id: "comision_mercantil", nombre: "Comisión mercantil", porcentaje: 10 },
    { id: "fabricacion_bienes_encargo", nombre: "Fabricación de bienes por encargo", porcentaje: 10 },
    { id: "transporte_personas", nombre: "Servicio de transporte de personas", porcentaje: 10 },
    { id: "contratos_construccion", nombre: "Contratos de construcción", porcentaje: 4 },
    { id: "demas_servicios_igv", nombre: "Demás servicios gravados con el IGV", porcentaje: 12 },
];

const FORM_INICIAL = {
    periodo: "",
    numero_factura: "",
    servicio_id: "",
    proveedor_id: "",
    proveedor: "",
    ruc: "",
    descripcion_servicio: "",
    tipo_detraccion: "",
    fecha_factura: "",
    monto_factura: "",
    porcentaje_detraccion: "",
    monto_detraccion: "",
    observacion: "",
    archivo_factura: null,
    archivo_pago: null,
};

function PagoDetracciones() {
    const [state] = useContext(AppContext);
    const [registros, setRegistros] = useState([]);
    const [servicios, setServicios] = useState([]);
    const [loading, setLoading] = useState(false);
    const [periodo, setPeriodo] = useState(() => new Date().toISOString().slice(0, 7));
    const [estado, setEstado] = useState("");
    const facturaRef = useRef(null);
    const pagoRef = useRef(null);
    const [modalFormulario, setModalFormulario] = useState(false);
    const [modalAccion, setModalAccion] = useState(false);
    const [registroSeleccionado, setRegistroSeleccionado] = useState(null);
    const [form, setForm] = useState({ ...FORM_INICIAL, periodo: new Date().toISOString().slice(0, 7) });
    const [accion, setAccion] = useState("");
    const [observacionAccion, setObservacionAccion] = useState("");
    const [fechaPago, setFechaPago] = useState(new Date().toISOString().slice(0, 10));
    const [archivoPago, setArchivoPago] = useState(null);
    const [resultadoFinal, setResultadoFinal] = useState("concluir");
    const [periodoSiguiente, setPeriodoSiguiente] = useState("");
    const [mensajeModal, setMensajeModal] = useState({ isOpen: false, titulo: "", mensaje: "", variante: "exito" });
    const [confirmacionModal, setConfirmacionModal] = useState({
        isOpen: false,
        titulo: "",
        mensaje: "",
        textConfirmar: "Confirmar",
        variante: "exito",
        onConfirmar: null,
    });
    const initialFetchDone = useRef(false);

    const perfilId = Number(state.user?.perfil_id || state.perfil_id);
    const puedeRegistrar = [8, 21, 26].includes(perfilId);
    const puedeAprobar = [26].includes(perfilId);

    const formatoSoles = (monto) => new Intl.NumberFormat("es-PE", {
        style: "currency",
        currency: "PEN",
    }).format(Number(monto || 0));

    const formatoFecha = (fecha) => {
        if (!fecha) return "-";

        const date = new Date(fecha);

        if (isNaN(date.getTime())) {
            return "-";
        }

        return date.toLocaleDateString("es-PE", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            timeZone: "UTC",
        });
    };

    const formatoNumeroPago = (id) => `PD-${String(id || 0).padStart(6, "0")}`;

    const calcularMontoDetraccion = (montoFactura, porcentaje) => {
        const monto = Number(montoFactura || 0);
        const tasa = Number(porcentaje || 0);

        if (!Number.isFinite(monto) || !Number.isFinite(tasa)) {
            return "";
        }

        return String(Math.round(monto * (tasa / 100)));
    };

    const obtenerTipoDetraccion = (id) => TIPOS_DETRACCION.find(item => item.id === id);

    const obtenerNombreTipoDetraccion = (id) => obtenerTipoDetraccion(id)?.nombre || "-";

    const totalDetracciones = useMemo(() => {
        return registros.reduce((acc, item) => acc + Number(item.monto_detraccion || 0), 0);
    }, [registros]);

    const mostrarMensaje = useCallback((titulo, mensaje, variante = "exito") => {
        setMensajeModal({ isOpen: true, titulo, mensaje, variante });
    }, []);

    const cerrarMensaje = () => {
        setMensajeModal(prev => ({ ...prev, isOpen: false }));
    };

    const obtenerMensajeError = (error, fallback) => {
        const errores = error.response?.data?.errors;

        if (errores) {
            return Object.values(errores).flat().join("\n");
        }

        return error.response?.data?.message || fallback;
    };

    const abrirConfirmacion = ({ titulo, mensaje, textConfirmar = "Confirmar", variante = "exito", onConfirmar }) => {
        setConfirmacionModal({
            isOpen: true,
            titulo,
            mensaje,
            textConfirmar,
            variante,
            onConfirmar,
        });
    };

    const cerrarConfirmacion = () => {
        setConfirmacionModal(prev => ({ ...prev, isOpen: false, onConfirmar: null }));
    };

    const confirmarAccionModal = async () => {
        const accionConfirmada = confirmacionModal.onConfirmar;
        cerrarConfirmacion();

        if (accionConfirmada) {
            await accionConfirmada();
        }
    };

    const cargarRegistros = useCallback(async () => {
        setLoading(true);
        try {
            const response = await PagoDetraccionesService.getPagoDetracciones(periodo, estado);
            setRegistros(response.data || []);
        } catch (error) {
            console.error("Error al cargar detracciones:", error);
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar detracciones"), "peligro");
            setRegistros([]);
        } finally {
            setLoading(false);
        }
    }, [periodo, estado, mostrarMensaje]);

    const cargarServicios = useCallback(async () => {
        try {
            const response = await ProveedorServiciosService.getServiciosProveedor();
            setServicios(response.data || []);
        } catch (error) {
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al cargar servicios"), "peligro");
            setServicios([]);
        }
    }, [mostrarMensaje]);

    useEffect(() => {
        if (!initialFetchDone.current) {
            cargarServicios();
            cargarRegistros();
            initialFetchDone.current = true;
        }
    }, [cargarServicios, cargarRegistros]);

    const abrirNuevo = () => {
        setRegistroSeleccionado(null);
        setForm({ ...FORM_INICIAL, periodo });
        setModalFormulario(true);
    };

    const abrirEditar = (registro) => {
        setRegistroSeleccionado(registro);
        setForm({
            periodo: registro.periodo || periodo,
            numero_factura: registro.numero_factura || "",
            servicio_id: registro.servicio_id || "",
            proveedor_id: registro.proveedor_id || "",
            proveedor: registro.proveedor || "",
            ruc: registro.ruc || "",
            descripcion_servicio: registro.descripcion_servicio || "",
            tipo_detraccion: registro.tipo_detraccion || "",
            fecha_factura: registro.fecha_factura ? String(registro.fecha_factura).slice(0, 10) : "",
            monto_factura: registro.monto_factura || "",
            porcentaje_detraccion: registro.porcentaje_detraccion || "",
            monto_detraccion: registro.monto_detraccion || "",
            observacion: registro.observacion || "",
            archivo_factura: null,
        });
        setModalFormulario(true);
    };

    const cerrarFormulario = () => {
        setModalFormulario(false);
        setRegistroSeleccionado(null);
        setForm({ ...FORM_INICIAL, periodo });
    };

    const handleFormChange = (e) => {
        const { name, value, files } = e.target;

        if (name === "servicio_id") {
            const servicio = servicios.find(item => Number(item.servicio_id) === Number(value));
            setForm(prev => ({
                ...prev,
                servicio_id: value,
                proveedor_id: servicio?.proveedor_id || "",
                proveedor: servicio?.nombre_razon_social || "",
                ruc: servicio?.documento || "",
                descripcion_servicio: servicio?.descripcion_servicio || "",
                monto_factura: servicio?.precio || "",
                monto_detraccion: calcularMontoDetraccion(servicio?.precio || "", prev.porcentaje_detraccion),
            }));
            return;
        }

        if (name === "tipo_detraccion") {
            const tipo = obtenerTipoDetraccion(value);
            setForm(prev => ({
                ...prev,
                tipo_detraccion: value,
                porcentaje_detraccion: tipo ? String(tipo.porcentaje) : "",
                monto_detraccion: calcularMontoDetraccion(prev.monto_factura, tipo?.porcentaje || ""),
            }));
            return;
        }

        setForm(prev => ({
            ...prev,
            [name]: files ? files[0] : value,
            ...(["monto_factura", "porcentaje_detraccion"].includes(name)
                ? {
                    monto_detraccion: calcularMontoDetraccion(
                        name === "monto_factura" ? value : prev.monto_factura,
                        name === "porcentaje_detraccion" ? value : prev.porcentaje_detraccion
                    ),
                }
                : {}),
        }));
    };

    const guardarRegistro = async () => {
        if (!form.periodo || !form.numero_factura || !form.servicio_id || !form.tipo_detraccion || !form.monto_factura || !form.porcentaje_detraccion || form.monto_detraccion === "") {
            mostrarMensaje("Validación", "Complete los campos obligatorios", "peligro");
            return;
        }

        if (Number(form.monto_factura || 0) <= MONTO_MINIMO_DETRACCION) {
            mostrarMensaje("Validación", "El monto de la operación debe ser mayor a S/ 700.00 para aplicar detracción", "peligro");
            return;
        }

        setLoading(true);
        try {
            await PagoDetraccionesService.guardarPagoDetraccion(form, registroSeleccionado?.detraccion_id);
            cerrarFormulario();
            await cargarRegistros();
        } catch (error) {
            console.error("Error al guardar detracción:", error);
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al guardar el registro"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const confirmarGuardarRegistro = () => {
        abrirConfirmacion({
            titulo: "Confirmar registro",
            mensaje: registroSeleccionado ? "¿Está seguro de actualizar esta factura?" : "¿Está seguro de guardar esta factura?",
            textConfirmar: "Guardar",
            variante: "exito",
            onConfirmar: guardarRegistro,
        });
    };

    const abrirAccion = (tipo, registro) => {
        setAccion(tipo);
        setRegistroSeleccionado(registro);
        setObservacionAccion("");
        setFechaPago(new Date().toISOString().slice(0, 10));
        setArchivoPago(null);
        setResultadoFinal("concluir");
        
        const fechaActual = new Date();
        const siguienteMes = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + 1, 1);
        setPeriodoSiguiente(siguienteMes.toISOString().slice(0, 7));
        
        setModalAccion(true);
    };

    const cerrarAccion = () => {
        setModalAccion(false);
        setRegistroSeleccionado(null);
        setAccion("");
    };

    const ejecutarAccion = async () => {
        if (!registroSeleccionado) return;

        setLoading(true);
        try {
            if (accion === "aprobar") {
                await PagoDetraccionesService.aprobarPagoDetraccion(registroSeleccionado.detraccion_id, observacionAccion);
            }

            if (accion === "observar") {
                if (!observacionAccion.trim()) {
                    mostrarMensaje("Validación", "Ingrese una observación", "peligro");
                    setLoading(false);
                    return;
                }
                await PagoDetraccionesService.observarPagoDetraccion(registroSeleccionado.detraccion_id, observacionAccion);
            }

            if (accion === "pagar") {
                await PagoDetraccionesService.marcarPagoRealizado(registroSeleccionado.detraccion_id, fechaPago, archivoPago);
            }

            if (accion === "validar") {
                await PagoDetraccionesService.validarPagoDetraccion(registroSeleccionado.detraccion_id, {
                    resultado: resultadoFinal,
                    observacion_final: observacionAccion,
                    periodo_siguiente: resultadoFinal === "observar" && periodoSiguiente ? `${periodoSiguiente}-01` : "",
                });
            }

            cerrarAccion();
            await cargarRegistros();
        } catch (error) {
            console.error("Error al ejecutar acción:", error);
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al procesar la acción"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const confirmarEjecutarAccion = () => {
        abrirConfirmacion({
            titulo: "Confirmar acción",
            mensaje: "¿Está seguro de realizar esta acción?",
            textConfirmar: "Confirmar",
            variante: "exito",
            onConfirmar: ejecutarAccion,
        });
    };

    const anularRegistro = async (registro) => {
        abrirConfirmacion({
            titulo: "Anular factura",
            mensaje: `¿Está seguro de anular la factura ${registro.numero_factura}?`,
            textConfirmar: "Anular",
            variante: "peligro",
            onConfirmar: () => ejecutarAnulacion(registro),
        });
    };

    const ejecutarAnulacion = async (registro) => {
        setLoading(true);
        try {
            await PagoDetraccionesService.anularPagoDetraccion(registro.detraccion_id);
            await cargarRegistros();
        } catch (error) {
            console.error("Error al anular detracción:", error);
            mostrarMensaje("Error", obtenerMensajeError(error, "Error al anular el registro"), "peligro");
        } finally {
            setLoading(false);
        }
    };

    const verFactura = (registro) => {
        if (!registro.archivo_factura_url) {
            mostrarMensaje("Aviso", "No hay factura adjunta", "peligro");
            return;
        }
        window.open(registro.archivo_factura_url, "_blank");
    };

    const puedeEditar = (registro) => puedeRegistrar && [1, 3].includes(Number(registro.estado_proceso));
    const puedePagar = (registro) => puedeRegistrar && Number(registro.estado_proceso) === 2;
    const puedeValidar = (registro) => puedeRegistrar && Number(registro.estado_proceso) === 4;
    const puedeAnular = (registro) => puedeRegistrar && ![4, 5, 6].includes(Number(registro.estado_proceso));
    const claseFilaEstado = (estadoProceso) => {
        const estadoActual = Number(estadoProceso);

        if ([3, 6].includes(estadoActual)) {
            return "fila-observada";
        }

        if (estadoActual === 5) {
            return "fila-concluida";
        }

        return "";
    };

    return (
        <div className="caja-flujo-container pago-detracciones-scope">
            {loading && <Loader />}

            <div className="flex items-center justify-between mb-4">
                <h1>PAGO DE DETRACCIONES</h1>
            </div>

            <div className="filtros">
                <div className="campo-fecha">
                    <Label>Periodo:</Label>
                    <Input type="month" value={periodo} onChange={(e) => setPeriodo(e.target.value)} />
                </div>

                <div className="campo-fecha">
                    <Label>Estado:</Label>
                    <Input type="select" value={estado} onChange={(e) => setEstado(e.target.value)}>
                        <option value="">Todos los estados</option>
                        {ESTADOS.map(item => (
                            <option key={item.id} value={item.id}>{item.nombre}</option>
                        ))}
                    </Input>
                </div>
            </div>

            <ButtonSearch onClick={cargarRegistros} isLoading={loading} />

            <div className="pago-detracciones-toolbar">
                {puedeRegistrar && <ButtonInsert onClick={abrirNuevo} />}
            </div>

            <div className="numero-propuestas">
                <p>N° registros: {registros.length}</p>
                <p>Total detracciones: <span>{formatoSoles(totalDetracciones)}</span></p>
            </div>

            <div className="table-container">
                <Table>
                    <thead>
                        <tr>
                            <th>N° PAGO DETRACCIÓN</th>
                            <th>FACTURA</th>
                            <th>PROVEEDOR</th>
                            <th>RUC</th>
                            <th>SERVICIO / DESCRIPCIÓN</th>
                            <th>TIPO DETRACCIÓN</th>
                            <th>FECHA FACTURA</th>
                            <th>FECHA CREACIÓN</th>
                            <th>MONTO FACTURA</th>
                            <th>%</th>
                            <th>DETRACCIÓN</th>
                            <th className="estado-col">ESTADO</th>
                            <th className="observacion-col">OBSERVACIÓN</th>
                            <th>ARCHIVO FACTURA</th>
                            <th>COMPROBANTE PAGO</th>
                            <th>ACCIONES</th>
                        </tr>
                    </thead>
                    <tbody>
                        {registros.length === 0 ? (
                            <tr>
                                <td colSpan="16" className="sin-data">No hay detracciones para el periodo seleccionado</td>
                            </tr>
                        ) : registros.map((registro) => (
                            <tr key={registro.detraccion_id} className={claseFilaEstado(registro.estado_proceso)}>
                                <td>{registro.numero_pago_detraccion || formatoNumeroPago(registro.detraccion_id)}</td>
                                <td>{registro.numero_factura}</td>
                                <td>{registro.proveedor}</td>
                                <td>{registro.ruc || "-"}</td>
                                <td className="descripcion-col">{registro.descripcion_servicio || "-"}</td>
                                <td className="tipo-detraccion-col">{obtenerNombreTipoDetraccion(registro.tipo_detraccion)}</td>
                                <td>{formatoFecha(registro.fecha_factura)}</td>
                                <td>{formatoFecha(registro.created_at)}</td>
                                <td>{formatoSoles(registro.monto_factura)}</td>
                                <td>{Number(registro.porcentaje_detraccion || 0)}%</td>
                                <td>{formatoSoles(registro.monto_detraccion)}</td>
                                <td className="estado-proceso">{ESTADOS.find(e => e.id === Number(registro.estado_proceso))?.nombre || 'Desconocido'}</td>
                                <td className="observacion-col">{registro.observacion_aprobacion || registro.observacion_final || registro.observacion || "-"}</td>
                                <td className="documento-cell">
                                    {registro.archivo_factura_url ? (
                                        <button 
                                            type="button" 
                                            className="documento-icono" 
                                            onClick={() => verFactura(registro)} 
                                            title="Ver factura"
                                        >
                                            <FileMinus size={20} style={{ color: '#3cb167' }} />
                                        </button>
                                    ) : (
                                        <div className="documento-icono sin-archivo">
                                            <FileMinus size={20} style={{ color: '#f87171', opacity: 0.7 }} title="Sin factura" />
                                        </div>
                                    )}
                                </td>
                                <td className="documento-cell">
                                    {registro.archivo_pago_url ? (
                                        <button 
                                            type="button" 
                                            className="documento-icono" 
                                            onClick={() => window.open(registro.archivo_pago_url, '_blank')} 
                                            title="Ver comprobante"
                                        >
                                            <FileMinus size={20} style={{ color: '#3cb167' }} />
                                        </button>
                                    ) : (
                                        <div className="documento-icono sin-archivo">
                                            <FileMinus size={20} style={{ color: '#f87171', opacity: 0.7 }} title="Sin comprobante" />
                                        </div>
                                    )}
                                </td>
                                <td>
                                    <div className="acciones-container">
                                        {puedeEditar(registro) && <ButtonUpdate onClick={() => abrirEditar(registro)} />}
                                        {puedeAprobar && Number(registro.estado_proceso) === 1 && (
                                            <>
                                                <button type="button" className="boton-aprobar" onClick={() => abrirAccion("aprobar", registro)} title="Aprobar">
                                                    Aprobar
                                                </button>
                                                <button type="button" className="boton-observar" onClick={() => abrirAccion("observar", registro)} title="Observar">
                                                    Observar
                                                </button>
                                            </>
                                        )}
                                        {puedePagar(registro) && (
                                            <button type="button" className="boton-pagar" onClick={() => abrirAccion("pagar", registro)} title="Registrar pago">
                                                Pagar
                                            </button>
                                        )}
                                        {puedeValidar(registro) && (
                                            <ButtonValidar onClick={() => abrirAccion("validar", registro)} />
                                        )}
                                        {puedeAnular(registro) && <ButtonDelete onClick={() => anularRegistro(registro)} />}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </div>

            <Modal isOpen={modalFormulario} className="custom-modal" backdrop="static" size="lg" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>{registroSeleccionado ? "EDITAR FACTURA" : "REGISTRAR FACTURA"}</h1>
                        <Button className="close-btn" onClick={cerrarFormulario}>X</Button>
                    </div>
                </ModalHeader>

                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            <FormGroup>
                                <Label>Periodo:</Label>
                                <Input className="form-control" type="month" name="periodo" value={form.periodo} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Número de factura:</Label>
                                <Input className="form-control" name="numero_factura" value={form.numero_factura} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Servicio:</Label>
                                <Input className="form-control" type="select" name="servicio_id" value={form.servicio_id} onChange={handleFormChange}>
                                    <option value="">Seleccione</option>
                                    {servicios.map(servicio => (
                                        <option key={servicio.servicio_id} value={servicio.servicio_id}>
                                            {servicio.descripcion_servicio} - {servicio.nombre_razon_social} - S/. {Number(servicio.precio || 0).toFixed(2)}
                                        </option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>Proveedor:</Label>
                                <Input className="form-control" name="proveedor" value={form.proveedor} disabled />
                            </FormGroup>
                            <FormGroup>
                                <Label>RUC:</Label>
                                <Input className="form-control" name="ruc" value={form.ruc} disabled />
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Servicio / descripción:</Label>
                                <Input className="form-control" type="textarea" name="descripcion_servicio" value={form.descripcion_servicio} disabled />
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Tipo de detracción:</Label>
                                <Input className="form-control" type="select" name="tipo_detraccion" value={form.tipo_detraccion} onChange={handleFormChange}>
                                    <option value="">Seleccione</option>
                                    {TIPOS_DETRACCION.map(tipo => (
                                        <option key={tipo.id} value={tipo.id}>{tipo.nombre} - {tipo.porcentaje}%</option>
                                    ))}
                                </Input>
                            </FormGroup>
                            <FormGroup>
                                <Label>Fecha factura:</Label>
                                <Input className="form-control" type="date" name="fecha_factura" value={form.fecha_factura} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup>
                                <Label>Monto factura:</Label>
                                <div className="input-group custom-input-group">
                                    <span className="input-group-text custom-symbol">S/.</span>
                                    <Input className="form-control" type="number" step="0.01" name="monto_factura" value={form.monto_factura} disabled />
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Porcentaje detracción:</Label>
                                <div className="input-group custom-input-group">
                                    <Input className="form-control" type="number" step="0.01" min="0" max="100" name="porcentaje_detraccion" value={form.porcentaje_detraccion} disabled />
                                    <span className="input-group-text custom-symbol">%</span>
                                </div>
                            </FormGroup>
                            <FormGroup>
                                <Label>Monto detracción:</Label>
                                <div className="input-group custom-input-group">
                                    <span className="input-group-text custom-symbol">S/.</span>
                                    <Input className="form-control" type="number" step="0.01" min="0" name="monto_detraccion" value={form.monto_detraccion} onChange={handleFormChange} />
                                </div>
                            </FormGroup>
                            <FormGroup className="mb-4">
                                <Label>Factura adjunta (PDF, IMG):</Label>
                                <div className="cap-upload-zone">
                                    <span className="cap-upload-btn" onClick={() => facturaRef.current?.click()}>
                                        {form.archivo_factura ? form.archivo_factura.name : 'Seleccionar archivo (.pdf, .jpg, .png)'}
                                    </span>
                                    <input
                                        ref={facturaRef}
                                        type="file"
                                        name="archivo_factura"
                                        accept=".pdf,.jpg,.jpeg,.png"
                                        style={{ display: 'none' }}
                                        onChange={handleFormChange}
                                    />
                                </div>
                                {form.archivo_factura && (
                                    <small>
                                        <span
                                            style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }}
                                            onClick={() => {
                                                setForm(prev => ({ ...prev, archivo_factura: null }));
                                                if (facturaRef.current) facturaRef.current.value = '';
                                            }}
                                        >
                                            ✕ Quitar selección
                                        </span>
                                    </small>
                                )}
                                {registroSeleccionado?.archivo_factura_url && !form.archivo_factura && (
                                    <small className="text-success mt-1 d-block">
                                        ✓ Ya existe una factura subida.{' '}
                                        <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(registroSeleccionado.archivo_factura_url, '_blank')}>Ver documento</span>
                                    </small>
                                )}
                            </FormGroup>
                            <FormGroup className="full-width">
                                <Label>Observación:</Label>
                                <Input className="form-control" type="textarea" name="observacion" value={form.observacion} onChange={handleFormChange} />
                            </FormGroup>
                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={confirmarGuardarRegistro}>
                                        GUARDAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>

            <Modal isOpen={modalAccion} className="custom-modal" backdrop="static" fade={false}>
                <ModalHeader className="modal-header">
                    <div className="titulo">
                        <h1>
                            {accion === "aprobar" && "APROBAR FACTURA"}
                            {accion === "observar" && "OBSERVAR FACTURA"}
                            {accion === "pagar" && "REGISTRAR PAGO"}
                            {accion === "validar" && "VALIDACIÓN FINAL"}
                        </h1>
                        <Button className="close-btn" onClick={cerrarAccion}>X</Button>
                    </div>
                </ModalHeader>

                <div className="modal-body-footer">
                    <ModalBody className="modal-body">
                        <Form className="fila" onSubmit={(e) => e.preventDefault()}>
                            {accion === "pagar" && (
                                <>
                                    <FormGroup>
                                        <Label>Fecha de pago:</Label>
                                        <Input className="form-control" type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} />
                                    </FormGroup>
                                    <FormGroup className="mb-4">
                                        <Label>Comprobante de pago (PDF, IMG):</Label>
                                        <div className="cap-upload-zone">
                                            <span className="cap-upload-btn" onClick={() => pagoRef.current?.click()}>
                                                {archivoPago ? archivoPago.name : 'Seleccionar archivo (.pdf, .jpg, .png)'}
                                            </span>
                                            <input
                                                ref={pagoRef}
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                style={{ display: 'none' }}
                                                onChange={(e) => setArchivoPago(e.target.files[0] || null)}
                                            />
                                        </div>
                                        {archivoPago && (
                                            <small>
                                                <span
                                                    style={{ cursor: 'pointer', color: '#f87171', fontSize: '10px' }}
                                                    onClick={() => {
                                                        setArchivoPago(null);
                                                        if (pagoRef.current) pagoRef.current.value = '';
                                                    }}
                                                >
                                                    ✕ Quitar selección
                                                </span>
                                            </small>
                                        )}
                                        {registroSeleccionado?.archivo_pago_url && !archivoPago && (
                                            <small className="text-success mt-1 d-block">
                                                ✓ Ya existe un comprobante subido.{' '}
                                                <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => window.open(registroSeleccionado.archivo_pago_url, '_blank')}>Ver documento</span>
                                            </small>
                                        )}
                                    </FormGroup>
                                </>
                            )}

                            {accion === "validar" && (
                                <>
                                    <FormGroup>
                                        <Label>Resultado:</Label>
                                        <Input className="form-control" type="select" value={resultadoFinal} onChange={(e) => {
                                            const valor = e.target.value;
                                            setResultadoFinal(valor);
                                            if (valor === "observar") {
                                                const fechaActual = new Date();
                                                const siguienteMes = new Date(fechaActual.getFullYear(), fechaActual.getMonth() + 1, 1);
                                                setPeriodoSiguiente(siguienteMes.toISOString().slice(0, 7));
                                            }
                                        }}>
                                            <option value="concluir">Concluir proceso</option>
                                            <option value="observar">Considerar en siguiente periodo</option>
                                        </Input>
                                    </FormGroup>
                                    {resultadoFinal === "observar" && (
                                        <FormGroup>
                                            <Label>Periodo siguiente:</Label>
                                            <Input className="form-control" type="month" value={periodoSiguiente} onChange={(e) => setPeriodoSiguiente(e.target.value)} />
                                        </FormGroup>
                                    )}
                                </>
                            )}

                            {(accion === "observar" || accion === "aprobar" || accion === "validar") && (
                                <FormGroup className="full-width">
                                    <Label>Observación:</Label>
                                    <Input className="form-control" type="textarea" value={observacionAccion} onChange={(e) => setObservacionAccion(e.target.value)} />
                                </FormGroup>
                            )}

                            <FormGroup>
                                <div className="button-container">
                                    <Button className="button-custom" onClick={confirmarEjecutarAccion}>
                                        CONFIRMAR
                                    </Button>
                                </div>
                            </FormGroup>
                        </Form>
                    </ModalBody>
                </div>
            </Modal>
            <ModalConfirmar
                isOpen={mensajeModal.isOpen}
                titulo={mensajeModal.titulo}
                mensaje={mensajeModal.mensaje}
                textConfirmar="Aceptar"
                textCancelar="Cerrar"
                variante={mensajeModal.variante}
                onConfirmar={cerrarMensaje}
                onCancelar={cerrarMensaje}
            />
            <ModalConfirmar
                isOpen={confirmacionModal.isOpen}
                titulo={confirmacionModal.titulo}
                mensaje={confirmacionModal.mensaje}
                textConfirmar={confirmacionModal.textConfirmar}
                textCancelar="Cancelar"
                variante={confirmacionModal.variante}
                onConfirmar={confirmarAccionModal}
                onCancelar={cerrarConfirmacion}
            />
        </div>
    );
}

export default PagoDetracciones;
